// ============================================================================
//  A dialog that does not disappear when you click past its edge
// ============================================================================
//
//  Every dialog in the claims workspace used to close on a backdrop click. On a
//  viewer that costs nothing. On Multi Vet it costs an afternoon: the pattern,
//  the ticked claims, the basket and the mappings are all held in the dialog and
//  NONE of it is written until you press apply, so one stray click at the wrong
//  moment threw the lot away — no warning, no undo, and nothing on screen to say
//  what had just happened.
//
//  So the backdrop no longer closes anything. The ✕ and the Close button are the
//  way out, and they are always visible.
//
//  A backdrop click is not silently swallowed either. Doing nothing at all reads
//  as a frozen screen, so the panel gives one short nudge — enough to say "the
//  click landed, but not that way", the same thing a macOS sheet does. Anyone who
//  clicks outside once out of habit sees immediately that the dialog is staying
//  put on purpose.
//
//  Usage — two props, no other change to a dialog's markup:
//
//      const { panelRef, backdropProps } = useDialogGuard();
//      <div role="dialog" {...backdropProps} style={backdrop}>
//        <div ref={panelRef} style={panel}> … </div>
//      </div>
//
import { useCallback, useRef } from 'react';

const NUDGE = 'tc-dialog-nudge';

//  Small enough to read as a nudge rather than an error. An error shake is wider
//  and faster; this settles back, because nothing has gone wrong — the dialog is
//  simply staying open.
//
//  It scales DOWN and back, never up. Two of these panels sit inside a backdrop
//  with `overflow: auto` (they scroll when taller than the screen), and a panel
//  that grows even 1% wider than its box can flash a scrollbar across the whole
//  dialog for the length of the animation. Inward it can never overflow, and a
//  slight press-in is the better read for "that did nothing" anyway.
const CSS = `
@keyframes ${NUDGE}-kf {
  0%   { transform: scale(1); }
  35%  { transform: scale(.992); }
  100% { transform: scale(1); }
}
.${NUDGE} { animation: ${NUDGE}-kf 220ms cubic-bezier(.34,1.56,.64,1); }

/*  With motion turned down, say it with light instead of movement.
    An OUTLINE, not a box-shadow: every panel sets its own shadow and each one is
    different, so animating that property would visibly restyle the dialog's edge
    for the length of the nudge. Nothing here sets an outline, and an outline
    takes no space, so it cannot shift or overflow anything either. */
@media (prefers-reduced-motion: reduce) {
  @keyframes ${NUDGE}-kf {
    0%, 100% { outline-color: rgba(33,122,60,0); }
    40%      { outline-color: rgba(33,122,60,.55); }
  }
  .${NUDGE} {
    outline: 2px solid rgba(33,122,60,0);
    outline-offset: 2px;
    animation: ${NUDGE}-kf 420ms ease-out;
  }
}
`;

let injected = false;
function ensureCss() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const tag = document.createElement('style');
  tag.setAttribute('data-tc', 'dialog-guard');
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

//  Exported on its own so it can be tested without a browser: everything that
//  decides ANYTHING lives here, and the hook below is only the plumbing.
//  Returns true when it nudged, which is what the test asserts on.
export function pressedBackdrop(e, el) {
  //  Only a press that BEGAN on the backdrop itself counts. Two reasons this is
  //  checked on mousedown and by target, not by letting a click bubble up:
  //
  //    · Select text inside the panel, drag, and release over the backdrop and
  //      the browser fires a click on the BACKDROP. That is precisely the stray
  //      event that used to close these dialogs mid-sentence, and it must not
  //      read as "get out" now either.
  //    · A child that stops propagation would silently disable the check;
  //      comparing target to currentTarget does not care what bubbles.
  if (!e || e.target !== e.currentTarget) return false;
  if (!el || !el.classList) return false;
  el.classList.remove(NUDGE);
  //  Reading a layout property forces the removal to flush before the class goes
  //  back on. Without it the browser coalesces both mutations and the animation
  //  never restarts — so a SECOND click outside would sit there doing nothing,
  //  which is the exact "is this frozen?" feeling the nudge exists to prevent.
  void el.offsetWidth;
  el.classList.add(NUDGE);
  return true;
}

export default function useDialogGuard() {
  const panelRef = useRef(null);
  ensureCss();
  const onMouseDown = useCallback((e) => { pressedBackdrop(e, panelRef.current); }, []);
  return { panelRef, backdropProps: { onMouseDown } };
}
